import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Plus, Search, Edit3, Trash2, BellOff, X,
    Activity, Shield, Users, UserCheck,
    ChevronRight, CheckCircle2, Circle, Filter, Info
} from 'lucide-react';
import {
    getNotificationSettingsRequest,
    createNotificationSettingRequest,
    updateNotificationSettingRequest,
    deleteNotificationSettingRequest
} from '../../../../redux/actions/notificationSettingsActions';
import { getAllModulesRequest } from '../../../../redux/actions/moduleActions';
import { getActiveRolesRequest } from '../../../../redux/actions/roleActions';
import { getAllUsersRequest } from '../../../../redux/actions/userActions';
import Select from 'react-select';
import Swal from 'sweetalert2';
import './NotificationSettings.css';

const TARGET_CONFIG = {
    DYNAMIC: {
        label: 'Destinataire dynamique',
        description: "Sélectionné dynamiquement par le service métier",
        class: 'ns-badge-blue', cardClass: 'ns-target-card-blue', icon: Activity
    },
    SPECIFIC_ROLE: {
        label: 'Rôle spécifique',
        description: 'Tous les utilisateurs ayant un ou plusieurs rôles donnés',
        class: 'ns-badge-orange', cardClass: 'ns-target-card-orange', icon: Shield
    },
    SPECIFIC_USER: {
        label: 'Utilisateur(s)',
        description: 'Sélection manuelle d\'un ou plusieurs utilisateurs',
        class: 'ns-badge-green', cardClass: 'ns-target-card-green', icon: Users
    },
};

const EMPTY_FORM = {
    eventCode: '', eventName: '', inAppEnabled: true,
    inAppTitleTemplate: '', inAppBodyTemplate: '',
    routePath: '', icon: 'bell',
    // ← tableau au lieu d'une string unique
    targetTypes: ['DYNAMIC'],
    moduleId: '', targetRoleIds: [], targetUserIds: [],
    priority: 'INFO', isActive: true
};

const NotificationSettings = () => {
    const dispatch = useDispatch();
    const { settings, loading } = useSelector(state => state.notificationSettings || { settings: [], loading: false });
    const { modules } = useSelector(state => state.module || { modules: [] });
    const { roles } = useSelector(state => state.role || { roles: [] });
    const { users } = useSelector(state => state.user || { users: [] });

    const [q, setQ] = useState('');
    const [statusFilter, setStatusFilter] = useState('ALL');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingSetting, setEditingSetting] = useState(null);
    const [modalStep, setModalStep] = useState(1);
    const [formData, setFormData] = useState(EMPTY_FORM);

    // ── helpers ──────────────────────────────────────────────────────────────
    const needsRolePicker = formData.targetTypes.some(t => ['SPECIFIC_ROLE', 'SPECIFIC_USER'].includes(t));
    const needsUserPicker = formData.targetTypes.some(t => ['SPECIFIC_ROLE', 'SPECIFIC_USER'].includes(t));
    const hasOnlySimpleTargets = formData.targetTypes.length > 0 &&
        formData.targetTypes.every(t => ['DYNAMIC'].includes(t));

    // show the "no manual selection needed" banner only when ALL selected types are simple
    const showSimpleInfo = hasOnlySimpleTargets;

    const availableRoles = useMemo(() => {
        if (!formData.moduleId) return roles;
        return roles.filter(r => r.moduleId === parseInt(formData.moduleId));
    }, [roles, formData.moduleId]);

    const availableUsersForSelect = useMemo(() => {
        let filtered = users;
        if (formData.moduleId)
            filtered = filtered.filter(u => u.roles?.some(r => r.moduleId === parseInt(formData.moduleId)));
        if (formData.targetRoleIds?.length > 0)
            filtered = filtered.filter(u => u.roles?.some(r => formData.targetRoleIds.includes(r.id)));
        return filtered.map(u => ({ value: u.id, label: u.fullName }));
    }, [users, formData.moduleId, formData.targetRoleIds]);

    const handleSelectAllUsers = () =>
        setFormData({ ...formData, targetUserIds: availableUsersForSelect.map(o => o.value) });

    // toggle a targetType in/out of the array
    const toggleTargetType = (key) => {
        const current = formData.targetTypes;
        const next = current.includes(key)
            ? current.filter(t => t !== key)
            : [...current, key];
        // reset role/user selections when removing complex types
        const stillNeedsRoles = next.some(t => ['SPECIFIC_ROLE', 'SPECIFIC_USER'].includes(t));
        setFormData({
            ...formData,
            targetTypes: next,
            targetRoleIds: stillNeedsRoles ? formData.targetRoleIds : [],
            targetUserIds: stillNeedsRoles ? formData.targetUserIds : [],
        });
    };

    useEffect(() => {
        dispatch(getNotificationSettingsRequest());
        dispatch(getAllModulesRequest());
        dispatch(getActiveRolesRequest());
        dispatch(getAllUsersRequest());
    }, [dispatch]);

    const filtered = useMemo(() => (settings || []).filter(s => {
        const matchesQ = !q ||
            s.eventCode.toLowerCase().includes(q.toLowerCase()) ||
            s.inAppTitleTemplate.toLowerCase().includes(q.toLowerCase());
        const matchesStatus = statusFilter === 'ALL' ||
            (statusFilter === 'ACTIVE' && s.isActive) ||
            (statusFilter === 'INACTIVE' && !s.isActive);
        return matchesQ && matchesStatus;
    }), [settings, q, statusFilter]);

    const handleOpenModal = (setting = null) => {
        if (setting) {
            setEditingSetting(setting);
            setFormData({
                eventCode: setting.eventCode,
                eventName: setting.eventName || '',
                inAppEnabled: setting.inAppEnabled,
                inAppTitleTemplate: setting.inAppTitleTemplate,
                inAppBodyTemplate: setting.inAppBodyTemplate,
                routePath: setting.routePath || '',
                icon: setting.icon || '',
                // support legacy single targetType OR new array targetTypes
                targetTypes: setting.targetTypes
                    ? setting.targetTypes
                    : setting.targetType
                        ? [setting.targetType]
                        : ['DYNAMIC'],
                moduleId: setting.moduleId || '',
                targetRoleIds: setting.targetRoles?.map(r => r.id) || [],
                targetUserIds: setting.targetUsers?.map(u => u.id) || [],
                priority: setting.priority || 'INFO',
                isActive: setting.isActive
            });
        } else {
            setEditingSetting(null);
            setFormData(EMPTY_FORM);
        }
        setModalStep(1);
        setIsModalOpen(true);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (editingSetting) dispatch(updateNotificationSettingRequest(editingSetting.id, formData));
        else dispatch(createNotificationSettingRequest(formData));
        setIsModalOpen(false);
    };

    const handleDelete = (id) => {
        Swal.fire({
            title: 'Supprimer la règle ?',
            text: "Cette action désactivera les notifications pour cet événement.",
            icon: 'warning', showCancelButton: true,
            confirmButtonColor: '#ee645f', cancelButtonColor: '#9ca3af',
            confirmButtonText: 'Oui, supprimer', cancelButtonText: 'Annuler'
        }).then(r => { if (r.isConfirmed) dispatch(deleteNotificationSettingRequest(id)); });
    };

    const handleToggleActive = (setting) => {
        dispatch(updateNotificationSettingRequest(setting.id, {
            ...setting,
            targetTypes: setting.targetTypes || (setting.targetType ? [setting.targetType] : ['REQUESTER']),
            targetRoleIds: setting.targetRoles?.map(r => r.id) || [],
            targetUserIds: setting.targetUsers?.map(u => u.id) || [],
            isActive: !setting.isActive
        }));
    };

    const toggleRole = (id) => {
        const next = formData.targetRoleIds.includes(id)
            ? formData.targetRoleIds.filter(x => x !== id)
            : [...formData.targetRoleIds, id];
        setFormData({ ...formData, targetRoleIds: next, targetUserIds: [] });
    };

    const step2Complete =
        formData.targetTypes.length > 0 && (
            hasOnlySimpleTargets ||
            (formData.targetTypes.includes('SPECIFIC_ROLE') && formData.targetRoleIds.length > 0) ||
            (formData.targetTypes.includes('SPECIFIC_USER') && formData.targetUserIds.length > 0)
        );

    // ── render badge list for table ──────────────────────────────────────────
    const renderTargetBadges = (setting) => {
        const types = setting.targetTypes || (setting.targetType ? [setting.targetType] : []);
        return (
            <div className="ns-badge-list">
                {types.map(type => {
                    const config = TARGET_CONFIG[type];
                    const Icon = config?.icon || Activity;
                    return (
                        <span key={type} className={`ns-badge ${config?.class || 'ns-badge-blue'}`}>
                            <Icon size={12} className="me-1" />
                            {config?.label || type}
                            {type === 'SPECIFIC_ROLE' && setting.targetRoles?.length > 0 &&
                                <span className="ns-target-sub"> · {setting.targetRoles.length} rôles</span>}
                            {type === 'SPECIFIC_USER' && setting.targetUsers?.length > 0 &&
                                <span className="ns-target-sub"> · {setting.targetUsers.length} util.</span>}
                        </span>
                    );
                })}
            </div>
        );
    };

    return (
        <div className="ns-page">
            {/* Header */}
            <div className="ns-header">
                <div>
                    <h2>Paramétrage des Notifications</h2>
                    <p className="ns-subtitle">Gérez les règles d'envoi et les templates dynamiques de la plateforme.</p>
                </div>
                <button className="ns-btn ns-btn-primary" onClick={() => handleOpenModal()}>
                    <Plus size={16} /> Nouvelle Règle
                </button>
            </div>

            {/* Toolbar */}
            <div className="ns-toolbar">
                <div className="ns-search-wrapper">
                    <Search className="ns-search-icon" size={16} />
                    <input type="text" className="ns-search-input"
                           placeholder="Rechercher un événement, un titre..."
                           value={q} onChange={e => setQ(e.target.value)} />
                </div>
                <div className="ns-filters">
                    {['ALL', 'ACTIVE', 'INACTIVE'].map(f => (
                        <button key={f} className={`ns-filter-tab ${statusFilter === f ? 'active' : ''}`}
                                onClick={() => setStatusFilter(f)}>
                            {f === 'ALL' ? 'Tous' : f === 'ACTIVE' ? 'Actifs' : 'Inactifs'}
                        </button>
                    ))}
                </div>
                <div className="ms-auto text-muted small fw-bold">
                    {filtered.length} Règle{filtered.length > 1 ? 's' : ''}
                </div>
            </div>

            {/* Table */}
            <div className="ns-table-wrapper">
                <table className="ns-table">
                    <thead>
                    <tr>
                        <th>Événement</th><th>Cible(s)</th><th>Module</th>
                        <th>Canaux</th><th>Statut</th><th className="text-end">Actions</th>
                    </tr>
                    </thead>
                    <tbody>
                    {filtered.map(setting => (
                        <tr key={setting.id}>
                            <td>
                                <div className="ns-icon-cell">
                                    <div className="ns-avatar"><Activity size={18} /></div>
                                    <div>
                                        <div className="fw-bold text-dark">{setting.eventCode}</div>
                                        <div className="text-muted small" style={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {setting.inAppTitleTemplate}
                                        </div>
                                    </div>
                                </div>
                            </td>
                            <td>{renderTargetBadges(setting)}</td>
                            <td><span className="text-muted small">{setting.moduleName || 'Global'}</span></td>
                            <td>{setting.inAppEnabled && <span className="ns-badge ns-badge-blue">In-App</span>}</td>
                            <td>
                                <div className={`ns-toggle ${setting.isActive ? 'on' : ''}`} onClick={() => handleToggleActive(setting)}>
                                    <div className="ns-toggle-box"><div className="ns-toggle-knob" /></div>
                                    <span className="ns-toggle-label">{setting.isActive ? 'Actif' : 'Inactif'}</span>
                                </div>
                            </td>
                            <td className="text-end">
                                <div className="d-flex justify-content-end gap-2">
                                    <button className="btn btn-sm btn-light border" onClick={() => handleOpenModal(setting)} title="Modifier">
                                        <Edit3 size={14} className="text-primary" />
                                    </button>
                                    <button className="btn btn-sm btn-light border text-danger" onClick={() => handleDelete(setting.id)} title="Supprimer">
                                        <Trash2 size={14} />
                                    </button>
                                </div>
                            </td>
                        </tr>
                    ))}
                    </tbody>
                </table>
                {filtered.length === 0 && !loading && (
                    <div className="ns-empty">
                        <BellOff size={48} className="mb-3 opacity-50" />
                        <p>Aucune règle de notification trouvée.</p>
                    </div>
                )}
            </div>

            {/* ===== MODAL ===== */}
            {isModalOpen && (
                <div className="ns-overlay" onClick={() => setIsModalOpen(false)}>
                    <div className="ns-modal" onClick={e => e.stopPropagation()}>

                        {/* Header fixe */}
                        <div className="ns-modal-header">
                            <div className="ns-modal-title-group">
                                <h3>{editingSetting ? 'Modifier la Règle' : 'Nouvelle Règle'}</h3>
                                <div className="ns-steps">
                                    <button type="button"
                                            className={`ns-step ${modalStep === 1 ? 'active' : 'done'}`}
                                            onClick={() => setModalStep(1)}>
                                        {modalStep > 1 ? <CheckCircle2 size={14} /> : <span>1</span>}
                                        <span>Événement & Template</span>
                                    </button>
                                    <ChevronRight size={14} className="ns-step-sep" />
                                    <button type="button"
                                            className={`ns-step ${modalStep === 2 ? 'active' : ''} ${step2Complete && modalStep !== 2 ? 'done' : ''}`}
                                            onClick={() => setModalStep(2)}>
                                        {step2Complete && modalStep !== 2 ? <CheckCircle2 size={14} /> : <span>2</span>}
                                        <span>Destinataires</span>
                                    </button>
                                </div>
                            </div>
                            <button className="ns-modal-close" onClick={() => setIsModalOpen(false)}><X size={20} /></button>
                        </div>

                        {/* Form : body scrollable + footer fixe */}
                        <form onSubmit={handleSubmit} className="ns-modal-form">
                            <div className="ns-modal-body">

                                {/* ——— STEP 1 ——— */}
                                {modalStep === 1 && (
                                    <div className="ns-form-grid">
                                        <div className="ns-form-group">
                                            <label>Code Événement *</label>
                                            <input type="text" className="ns-input" value={formData.eventCode}
                                                   onChange={e => setFormData({ ...formData, eventCode: e.target.value })}
                                                   placeholder="ex: NEW_ABSENCE_TYPE" required />
                                        </div>
                                        <div className="ns-form-group">
                                            <label>Nom de l'événement *</label>
                                            <input type="text" className="ns-input" value={formData.eventName}
                                                   onChange={e => setFormData({ ...formData, eventName: e.target.value })}
                                                   placeholder="ex: Création type de congé" required />
                                        </div>
                                        <div className="ns-form-group">
                                            <label>Icône (FontAwesome)</label>
                                            <input type="text" className="ns-input" value={formData.icon}
                                                   onChange={e => setFormData({ ...formData, icon: e.target.value })}
                                                   placeholder="ex: bell, calendar-alt" />
                                        </div>
                                        <div className="ns-form-group">
                                            <label>Route de redirection</label>
                                            <input type="text" className="ns-input" value={formData.routePath}
                                                   onChange={e => setFormData({ ...formData, routePath: e.target.value })}
                                                   placeholder="ex: /parametres/conges" />
                                        </div>
                                        <div className="ns-form-group full">
                                            <label>Titre de la Notification (Template) *</label>
                                            <input type="text" className="ns-input" value={formData.inAppTitleTemplate}
                                                   onChange={e => setFormData({ ...formData, inAppTitleTemplate: e.target.value })}
                                                   placeholder="ex: Nouveau type de congé : {{absence_name}}" required />
                                        </div>
                                        <div className="ns-form-group full">
                                            <label>Corps du message (Template) *</label>
                                            <textarea className="ns-textarea" rows="3" value={formData.inAppBodyTemplate}
                                                      onChange={e => setFormData({ ...formData, inAppBodyTemplate: e.target.value })}
                                                      placeholder="Générez un message dynamique avec {{variables}}..." required />
                                            {formData.availableVariables && (
                                                <div className="ns-vars-helper">
                                                    <span className="ns-vars-label">Variables disponibles :</span>
                                                    {formData.availableVariables.split(',').map(v => (
                                                        <code key={v} className="ns-var-chip"
                                                              onClick={() => navigator.clipboard.writeText(`{{${v.trim()}}}`)}>{v.trim()}</code>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* ——— STEP 2 ——— */}
                                {modalStep === 2 && (
                                    <div className="ns-target-section">

                                        {/* Multi-select hint */}
                                        <div className="ns-section-header">
                                            <div className="ns-section-label">Qui doit recevoir cette notification ?</div>
                                            <span className="ns-multi-hint">
                                                <CheckCircle2 size={11} />
                                                Sélection multiple possible
                                            </span>
                                        </div>

                                        {/* Cards type de cible — now multi-select */}
                                        <div className="ns-target-cards">
                                            {Object.entries(TARGET_CONFIG).map(([key, config]) => {
                                                const Icon = config.icon;
                                                const isSelected = formData.targetTypes.includes(key);
                                                return (
                                                    <button type="button" key={key}
                                                            className={`ns-target-card ${config.cardClass} ${isSelected ? 'selected' : ''}`}
                                                            onClick={() => toggleTargetType(key)}>
                                                        <div className="ns-target-card-icon"><Icon size={20} /></div>
                                                        <div className="ns-target-card-content">
                                                            <span className="ns-target-card-label">{config.label}</span>
                                                            <span className="ns-target-card-desc">{config.description}</span>
                                                        </div>
                                                        <div className="ns-target-card-check">
                                                            {isSelected ? <CheckCircle2 size={18} /> : <Circle size={18} />}
                                                        </div>
                                                    </button>
                                                );
                                            })}
                                        </div>

                                        {/* Selection summary chips */}
                                        {formData.targetTypes.length > 0 && (
                                            <div className="ns-selected-types-summary">
                                                <span className="ns-selected-types-label">Sélectionnés :</span>
                                                {formData.targetTypes.map(type => {
                                                    const config = TARGET_CONFIG[type];
                                                    const Icon = config?.icon || Activity;
                                                    return (
                                                        <span key={type} className={`ns-badge ${config?.class || 'ns-badge-blue'}`}>
                                                            <Icon size={11} className="me-1" />
                                                            {config?.label || type}
                                                            <button
                                                                type="button"
                                                                className="ns-badge-remove"
                                                                onClick={() => toggleTargetType(type)}
                                                                title={`Retirer ${config?.label}`}
                                                            >
                                                                <X size={10} />
                                                            </button>
                                                        </span>
                                                    );
                                                })}
                                            </div>
                                        )}

                                        {/* Info cibles automatiques */}
                                        {showSimpleInfo && (
                                            <div className="ns-simple-target-info">
                                                <Info size={15} />
                                                <span>
                                                    Les notifications seront envoyées automatiquement aux destinataires sélectionnés
                                                    (<strong>{formData.targetTypes.map(t => TARGET_CONFIG[t]?.label.toLowerCase()).join(', ')}</strong>)
                                                    lors de chaque déclenchement. Aucune sélection manuelle n'est requise.
                                                </span>
                                            </div>
                                        )}

                                        {/* Périmètre module — only shown when at least one complex type is selected */}
                                        {needsRolePicker && (
                                            <div className="ns-scope-block">
                                                <div className="ns-scope-header">
                                                    <Filter size={14} />
                                                    <span>Périmètre (Module)</span>
                                                    <span className="ns-scope-hint">Optionnel — restreint les rôles et utilisateurs disponibles</span>
                                                </div>
                                                <select className="ns-select" value={formData.moduleId}
                                                        onChange={e => setFormData({ ...formData, moduleId: e.target.value, targetRoleIds: [], targetUserIds: [] })}>
                                                    <option value="">Tous les modules</option>
                                                    {modules?.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                                                </select>
                                            </div>
                                        )}

                                        {/* ——— Picker Rôles ——— */}
                                        {needsRolePicker && (
                                            <div className="ns-picker-block">
                                                <div className="ns-picker-header">
                                                    <Shield size={14} />
                                                    <span>
                                                        {formData.targetTypes.includes('SPECIFIC_USER') && !formData.targetTypes.includes('SPECIFIC_ROLE')
                                                            ? 'Filtrer par rôle (optionnel)'
                                                            : 'Rôles destinataires *'}
                                                    </span>
                                                    {formData.moduleId && <span className="ns-picker-hint ns-picker-hint-active">Filtrés sur le module</span>}
                                                    <span className="ns-picker-count">{availableRoles.length} rôle{availableRoles.length !== 1 ? 's' : ''}</span>
                                                </div>

                                                {availableRoles.length === 0 ? (
                                                    <div className="ns-empty-picker"><Info size={14} /><span>Aucun rôle disponible pour ce périmètre.</span></div>
                                                ) : availableRoles.length <= 14 ? (
                                                    <div className="ns-chip-grid">
                                                        {availableRoles.map(r => {
                                                            const isActive = formData.targetRoleIds.includes(r.id);
                                                            return (
                                                                <button type="button" key={r.id}
                                                                        className={`ns-chip ${isActive ? 'active' : ''}`}
                                                                        onClick={() => toggleRole(r.id)}>
                                                                    {isActive ? <CheckCircle2 size={11} /> : <Circle size={11} />}
                                                                    {r.name || r.roleName}
                                                                </button>
                                                            );
                                                        })}
                                                    </div>
                                                ) : (
                                                    <Select isMulti
                                                            options={availableRoles.map(r => ({ value: r.id, label: r.name || r.roleName }))}
                                                            value={availableRoles.filter(r => formData.targetRoleIds.includes(r.id)).map(r => ({ value: r.id, label: r.name || r.roleName }))}
                                                            onChange={sel => setFormData({ ...formData, targetRoleIds: sel ? sel.map(s => s.value) : [], targetUserIds: [] })}
                                                            className="ns-react-select-container" classNamePrefix="ns-react-select"
                                                            placeholder="Choisir un ou plusieurs rôles..."
                                                            menuPortalTarget={document.body} menuPosition="fixed"
                                                            styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }} />
                                                )}

                                                {formData.targetRoleIds.length > 0 && (
                                                    <div className="ns-selection-summary">
                                                        <CheckCircle2 size={12} className="ns-summary-icon" />
                                                        <span>{formData.targetRoleIds.length} rôle{formData.targetRoleIds.length > 1 ? 's' : ''} sélectionné{formData.targetRoleIds.length > 1 ? 's' : ''}</span>
                                                        <button type="button" className="ns-clear-link"
                                                                onClick={() => setFormData({ ...formData, targetRoleIds: [], targetUserIds: [] })}>Effacer</button>
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {/* ——— Picker Utilisateurs ——— */}
                                        {needsUserPicker && (
                                            <div className="ns-picker-block">
                                                <div className="ns-picker-header">
                                                    <Users size={14} />
                                                    <span>
                                                        {formData.targetTypes.includes('SPECIFIC_ROLE') && !formData.targetTypes.includes('SPECIFIC_USER')
                                                            ? 'Utilisateurs supplémentaires (optionnel)'
                                                            : 'Utilisateurs destinataires *'}
                                                    </span>
                                                    {(formData.moduleId || formData.targetRoleIds.length > 0) && (
                                                        <span className="ns-picker-hint ns-picker-hint-active">
                                                            {formData.targetRoleIds.length > 0 ? 'Filtrés par rôle' : 'Filtrés par module'}
                                                        </span>
                                                    )}
                                                    <span className="ns-picker-count">{availableUsersForSelect.length} utilisateur{availableUsersForSelect.length !== 1 ? 's' : ''}</span>
                                                </div>

                                                {formData.targetTypes.includes('SPECIFIC_ROLE') && !formData.targetTypes.includes('SPECIFIC_USER') && (
                                                    <div className="ns-picker-note">
                                                        <Info size={12} />
                                                        <span>En plus des utilisateurs ayant les rôles sélectionnés, vous pouvez cibler des personnes spécifiques.</span>
                                                    </div>
                                                )}

                                                {availableUsersForSelect.length === 0 ? (
                                                    <div className="ns-empty-picker"><Info size={14} /><span>Aucun utilisateur ne correspond aux filtres appliqués.</span></div>
                                                ) : (
                                                    <>
                                                        <div className="ns-user-select-actions">
                                                            <button type="button" className="ns-btn-text" onClick={handleSelectAllUsers}>
                                                                Tout sélectionner ({availableUsersForSelect.length})
                                                            </button>
                                                            {formData.targetUserIds.length > 0 && (
                                                                <button type="button" className="ns-btn-text ns-btn-text-muted"
                                                                        onClick={() => setFormData({ ...formData, targetUserIds: [] })}>
                                                                    Tout désélectionner
                                                                </button>
                                                            )}
                                                        </div>
                                                        <Select isMulti
                                                                options={availableUsersForSelect}
                                                                value={availableUsersForSelect.filter(o => formData.targetUserIds.includes(o.value))}
                                                                onChange={sel => setFormData({ ...formData, targetUserIds: sel ? sel.map(s => s.value) : [] })}
                                                                className="ns-react-select-container" classNamePrefix="ns-react-select"
                                                                placeholder="Rechercher et sélectionner des utilisateurs..."
                                                                noOptionsMessage={() => "Aucun utilisateur trouvé"}
                                                                menuPortalTarget={document.body} menuPosition="fixed"
                                                                styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }} />
                                                    </>
                                                )}

                                                {formData.targetUserIds.length > 0 && (
                                                    <div className="ns-selection-summary">
                                                        <CheckCircle2 size={12} className="ns-summary-icon" />
                                                        <span>{formData.targetUserIds.length} utilisateur{formData.targetUserIds.length > 1 ? 's' : ''} sélectionné{formData.targetUserIds.length > 1 ? 's' : ''}</span>
                                                        <button type="button" className="ns-clear-link"
                                                                onClick={() => setFormData({ ...formData, targetUserIds: [] })}>Effacer</button>
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                    </div>
                                )}
                            </div>

                            {/* Footer fixe */}
                            <div className="ns-modal-footer">
                                {modalStep === 1 ? (
                                    <>
                                        <button type="button" className="ns-btn ns-btn-outline" onClick={() => setIsModalOpen(false)}>Annuler</button>
                                        {/*<button type="button" className="ns-btn ns-btn-primary"*/}
                                        {/*        onClick={() => setModalStep(2)}*/}
                                        {/*        disabled={!formData.eventCode || !formData.inAppTitleTemplate || !formData.inAppBodyTemplate}>*/}
                                        {/*    Suivant : Destinataires <ChevronRight size={15} />*/}
                                        {/*</button>*/}
                                    </>
                                ) : (
                                    <>
                                        <button type="button" className="ns-btn ns-btn-outline" onClick={() => setModalStep(1)}>← Retour</button>
                                        <button type="submit" className="ns-btn ns-btn-primary" disabled={!step2Complete}>
                                            {editingSetting ? 'Mettre à jour' : 'Créer la règle'}
                                        </button>
                                    </>
                                )}
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default NotificationSettings;